/* eslint-disable factorial/no-raw-html */
import * as React from 'react'

import { Box, Text, contentBreakpoints, dimensions } from '@factorialco/deprecated-design-system'
import { FormsTypeEnum } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { Participant, Question, Section } from 'modules/form_answers/lib/types'

import AnswerBox from '../AnswerBox'
import AnswersSidePanel from '../AnswersSidePanel'
import { EmptyState } from '../EmptyState'
import { ParticipantCell } from './ParticipantCell'

type TableData = {
  questions: Question[]
  participantsWithAnswers: Participant[]
  sections: Section[]
}
interface Props {
  data: TableData
  anonymous?: boolean
  formStatus: string
  formType: FormsTypeEnum
}

const AnswerCell: React.FC<{
  participant: Participant
  question: Question
  onClick: () => void
}> = ({ participant, question, onClick }) => {
  const answer = participant?.answers?.find((a) => a.questionId === question.id)?.options?.length
    ? participant.answers.find((a) => a.questionId === question.id)?.options
    : participant?.answers?.find((a) => a.questionId === question.id)?.answer || ''
  const comment =
    participant?.answers?.find((a) => a.questionId === question.id)?.comment ?? undefined

  return (
    <td>
      <Box width="s300" height="full" padding="s12">
        <AnswerBox
          type={question.questionType}
          question={question}
          answer={answer}
          onClick={onClick}
          comment={comment}
        />
      </Box>
    </td>
  )
}

const getSortedItems = (data: TableData) => {
  const sectionsWithQuestions = data.sections.map((section) => ({
    ...section,
    itemType: 'section' as const,
    questions: section.questions.map((question) => ({
      ...question,
      itemType: 'question' as const,
      position: question.position + 1,
    })),
  }))

  const standaloneQuestions = data.questions.map((question) => ({
    ...question,
    itemType: 'question' as const,
  }))

  const combinedItems = [...sectionsWithQuestions, ...standaloneQuestions]

  const sortedItems = combinedItems.sort((a, b) => {
    if (a.itemType === 'section' && b.itemType === 'section') {
      if (a.locked && !b.locked) return 1
      if (!a.locked && b.locked) return -1
    }
    if (a.itemType === 'section' && a.locked) return 1
    if (b.itemType === 'section' && b.locked) return -1

    return a.position - b.position
  })

  let currentPosition = 1
  const updatedItems = sortedItems.map((item) => {
    if (item.itemType === 'section' || item.itemType === 'question') {
      const updatedItem = { ...item, position: currentPosition }
      currentPosition++
      return updatedItem
    }
    return item
  })

  return updatedItems
}

const TableHeaders: React.FC<{ sortedItems: ReturnType<typeof getSortedItems> }> = ({
  sortedItems,
}) => (
  <>
    <th>
      <Box width="s300" padding="s12" textAlign="left">
        <Text.Normal size="200" weight="semibold">
          {i18n.t('form_answers.answers_table.participant')}
        </Text.Normal>
      </Box>
    </th>
    {sortedItems.flatMap((item) =>
      item.itemType === 'section'
        ? item.questions.map((question) => (
            <th key={question.id}>
              <Box width="s300" padding="s12" textAlign="left">
                <Text.Normal size="200" weight="semibold" lineClamp={4}>
                  {question.label}
                </Text.Normal>
              </Box>
            </th>
          ))
        : [
            <th key={item.id}>
              <Box width="s300" padding="s12" textAlign="left">
                <Text.Normal size="200" weight="semibold" lineClamp={4}>
                  {item.label}
                </Text.Normal>
              </Box>
            </th>,
          ]
    )}
  </>
)

export const AnswersTable: React.FC<Props> = ({
  formStatus,
  formType,
  data,
  anonymous = false,
}) => {
  const [openSidePanel, setOpenSidePanel] = React.useState(false)
  const [selectedParticipant, setSelectedParticipant] = React.useState<Participant | null>(null)
  const [selectedQuestion, setSelectedQuestion] = React.useState<Question | null>(null)

  const sortedItems = React.useMemo(() => getSortedItems(data), [data])

  const handleAnswerClick = (question: Question, participant: Participant) => {
    setOpenSidePanel(true)
    setSelectedQuestion(question)
    setSelectedParticipant(participant)
  }

  const windowHeight = window.innerHeight
  const windowWidth = window.innerWidth
  const { xxl } = contentBreakpoints
  const resizeTable = windowWidth > Number(xxl) ? `${windowHeight}px` : dimensions.s600

  return (
    <Box
      style={{
        maxHeight: resizeTable,
      }}
      overflowX="auto"
    >
      {data.participantsWithAnswers.length > 0 ? (
        <table cellSpacing="0" cellPadding="0">
          <thead>
            <tr>
              <TableHeaders sortedItems={sortedItems} />
            </tr>
          </thead>
          <tbody>
            {data.participantsWithAnswers.map((participant) => (
              <tr key={participant.participant?.id}>
                <th>
                  <Box
                    width="full"
                    height="full"
                    minHeight="s140"
                    alignItems="flexStart"
                    justifyContent="flexStart"
                    padding="s16"
                    cursor="pointer"
                    gap="s8"
                    onClick={() => {
                      setOpenSidePanel(true)
                      setSelectedParticipant(participant)
                    }}
                  >
                    <ParticipantCell participant={participant} anonymous={anonymous} />
                  </Box>
                </th>
                {sortedItems.flatMap((item) =>
                  item.itemType === 'section'
                    ? item.questions.map((question) => (
                        <AnswerCell
                          key={`${question.id}-${participant.participant?.fullName}`}
                          participant={participant}
                          question={question}
                          onClick={() => handleAnswerClick(question, participant)}
                        />
                      ))
                    : [
                        <AnswerCell
                          key={`${item.id}-${participant.participant?.fullName}`}
                          participant={participant}
                          question={item}
                          onClick={() => handleAnswerClick(item, participant)}
                        />,
                      ]
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState formStatus={formStatus} formType={formType} anonymous={anonymous} />
      )}
      {openSidePanel && (
        <Sidepanel
          open={openSidePanel}
          title={
            anonymous
              ? `${i18n.t('form_answers.answers_table.participant')} ${
                  selectedParticipant?.participant?.id
                }`
              : selectedQuestion?.label || selectedParticipant?.participant?.fullName || ''
          }
          onClose={() => {
            setOpenSidePanel(false)
            setSelectedQuestion(null)
          }}
          closeLabel={i18n.t('common.close')}
          allowBackgroundInteraction
          width="wideXL"
        >
          <AnswersSidePanel
            data={sortedItems}
            question={selectedQuestion}
            participant={selectedParticipant}
          />
        </Sidepanel>
      )}
    </Box>
  )
}
